import React, { useCallback, useMemo, useRef } from 'react';

import { IconButton } from '@material-ui/core';
import clsx from 'clsx';
import type { ReactNodeLike } from 'prop-types';
import { useTranslation } from 'react-i18next';

import { ReactComponent as MoreIcon } from 'assets/icons/more.svg';
import ContextMenu from 'components/ContextMenu/ContextMenu';
import useTableStyles from 'components/Table/Table.styles';
import type { AssetRopaReview } from 'config/api/ropaReviews/ropaReviews.types';
import processing_activities_messages from 'messages/processing_activities_messages';
import type { TableDataMappingRow } from 'types/Table';

import useStyles from '../ProcessingActivitiesAssetReviewsPage.styles';

type TableStyleClasses = {
  addonHeaderCell: string;
  clickableTr: string;
  noData: string;
  selectedRow: string;
  table: string;
  td: string;
  tdContent: string;
  th: string;
  thead: string;
  tr: string;
};

type ReviewStatusMessageKey = keyof typeof processing_activities_messages.asset_ropa.review_status;
type PermanentRopaStatusMessageKey = keyof typeof processing_activities_messages.asset_ropa.permanent_ropa_status;

export type AssetRopaAssetMenuAction =
  | 'open_asset_ropa'
  | 'request_clarification'
  | 'accept_all_submitted'
  | 'cancel_incomplete_asset_review';

type Props = {
  assets: AssetRopaReview[];
  emptyStateContent?: ReactNodeLike;
  expandedAssetId: number | null;
  hasActiveReview?: boolean;
  onAssetMenuAction: (asset: AssetRopaReview, action: AssetRopaAssetMenuAction) => void;
  onExpandAsset: (asset: AssetRopaReview) => void;
  renderExpandedAsset: (asset: AssetRopaReview) => React.ReactNode;
  showAdminAssetMenu: boolean;
};

const interactiveSelector = 'a, button, input, select, textarea, [role="button"], [role="link"]';

const ASSET_REVIEWS_COLUMN_WIDTHS: Record<string, number> = {
  assetName: 180,
  operationalOwnerName: 150,
  assetTypeName: 220,
  processingActivitiesCount: 150,
  permanentRopaStatus: 130,
  reviewStatus: 170,
  warnings: 260,
  actions: 56,
};

const shouldIgnoreRowActivation = (event: React.MouseEvent | React.KeyboardEvent) => {
  const interactiveElement = (event.target as HTMLElement | null)?.closest(interactiveSelector);

  return Boolean(interactiveElement && interactiveElement !== event.currentTarget);
};

const getReviewStatusDotClass = (status: AssetRopaReview['reviewStatus'], styles: ReturnType<typeof useStyles>) => {
  switch (status) {
    case 'accepted':
      return styles.statusDotSuccess;
    case 'submitted':
      return styles.statusDotInfo;
    case 'clarification_requested':
      return styles.statusDotDanger;
    default:
      return styles.statusDotWarning;
  }
};

const AssetRopaReviewsTable: React.FC<Props> = ({
  assets,
  emptyStateContent,
  expandedAssetId,
  hasActiveReview = false,
  onAssetMenuAction,
  onExpandAsset,
  renderExpandedAsset,
  showAdminAssetMenu,
}) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const tableStyles = useTableStyles({ fixedHeader: false, refetching: false, isPivoted: false }) as TableStyleClasses;
  const tableMessages = processing_activities_messages.asset_ropa.assets_table;
  const scrollContainerRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const isSyncingScrollRef = useRef(false);

  const showActionsColumn =
    showAdminAssetMenu &&
    assets.some(
      asset =>
        Boolean(asset.menuActions.accept_all_submitted || asset.menuActions.accept_all_submitted_reviews) ||
        Boolean(asset.menuActions.request_clarification) ||
        Boolean(asset.menuActions.cancel_incomplete_asset_review),
    );

  const dataMapping: TableDataMappingRow<AssetRopaReview>[] = useMemo(() => {
    const columns: TableDataMappingRow<AssetRopaReview>[] = [
      {
        id: 'assetName',
        label: t(tableMessages.asset),
        get: asset => (
          <span className={clsx(styles.assetNameLink, { [styles.assetNameLinkExpanded]: expandedAssetId === asset.id })}>
            {asset.assetName}
          </span>
        ),
      },
      {
        id: 'operationalOwnerName',
        label: t(tableMessages.owner),
        get: asset => asset.operationalOwnerName ?? t(processing_activities_messages.asset_ropa.panel.not_assigned),
      },
      {
        id: 'assetTypeName',
        label: t(tableMessages.asset_type),
        get: asset => asset.assetTypeName,
      },
      {
        id: 'processingActivitiesCount',
        label: t(tableMessages.processing_activities),
        get: asset => asset.processingActivitiesCount,
      },
      {
        id: 'permanentRopaStatus',
        label: t(tableMessages.ropa_status),
        get: asset => {
          const permanentStatusKey = asset.permanentRopaStatus as PermanentRopaStatusMessageKey;
          return processing_activities_messages.asset_ropa.permanent_ropa_status[permanentStatusKey]
            ? t(processing_activities_messages.asset_ropa.permanent_ropa_status[permanentStatusKey])
            : asset.permanentRopaStatus;
        },
      },
      {
        id: 'reviewStatus',
        label: t(tableMessages.review_status),
        get: asset => {
          const showNotIncluded = hasActiveReview && asset.includedInCurrentReview === false;
          const showNoHistory = !hasActiveReview && asset.hasReviewOverlay === false;

          if (showNoHistory) {
            return <span className={styles.statusCell}>{t(processing_activities_messages.asset_ropa.empty_state.no_review_history)}</span>;
          }

          if (showNotIncluded) {
            return (
              <span className={styles.statusCell}>
                {t(processing_activities_messages.asset_ropa.owner_workspace.panel.not_included_in_current_review)}
              </span>
            );
          }

          if (asset.hasReviewOverlay === false) {
            return <span className={styles.statusCell}>{t(processing_activities_messages.asset_ropa.empty_state.no_review_history)}</span>;
          }

          return (
            <span className={styles.statusCell}>
              <span className={clsx(styles.statusDot, getReviewStatusDotClass(asset.reviewStatus, styles))} />
              {t(processing_activities_messages.asset_ropa.review_status[asset.reviewStatus as ReviewStatusMessageKey])}
            </span>
          );
        },
      },
      {
        id: 'warnings',
        label: t(tableMessages.warnings),
        get: asset => {
          const warningChips = [
            asset.warnings.dpiaIndicated ? t(processing_activities_messages.asset_ropa.warnings.dpia_indicated) : null,
            asset.warnings.dpiaRequiredUnapproved ? t(processing_activities_messages.asset_ropa.warnings.dpia_required_unapproved) : null,
            asset.warnings.thirdCountryProcessing ? t(processing_activities_messages.asset_ropa.warnings.third_country_processing) : null,
            asset.warnings.sensitivePersonalData ? t(processing_activities_messages.asset_ropa.warnings.sensitive_personal_data) : null,
            asset.warnings.confirmedNoProcessingActivities
              ? t(processing_activities_messages.asset_ropa.warnings.confirmed_no_processing_activities)
              : null,
          ].filter(Boolean) as string[];

          if (warningChips.length === 0) {
            return '—';
          }

          return (
            <div className={styles.warningChipsCell}>
              {warningChips.map(chip => (
                <span key={chip} className={styles.warningChip}>
                  {chip}
                </span>
              ))}
            </div>
          );
        },
      },
    ];

    if (showActionsColumn) {
      columns.push({
        id: 'actions',
        blockSorting: true,
        isAddon: true,
        label: '',
        get: asset => {
          const menuItems = [
            ...(asset.menuActions.accept_all_submitted || asset.menuActions.accept_all_submitted_reviews
              ? [
                  {
                    label: t(processing_activities_messages.asset_ropa.actions.accept_all_submitted),
                    onClick: () => onAssetMenuAction(asset, 'accept_all_submitted'),
                  },
                ]
              : []),
            ...(asset.menuActions.request_clarification
              ? [
                  {
                    label: t(processing_activities_messages.asset_ropa.actions.request_clarification),
                    onClick: () => onAssetMenuAction(asset, 'request_clarification'),
                  },
                ]
              : []),
            ...(asset.menuActions.cancel_incomplete_asset_review
              ? [
                  {
                    label: t(processing_activities_messages.asset_ropa.actions.cancel_incomplete_asset_review),
                    onClick: () => onAssetMenuAction(asset, 'cancel_incomplete_asset_review'),
                  },
                ]
              : []),
          ];

          if (menuItems.length === 0) {
            return '';
          }

          return (
            <div className={styles.rowActions}>
              <ContextMenu elements={menuItems}>
                <IconButton aria-label={t(processing_activities_messages.asset_ropa.actions.asset_menu)} size='small'>
                  <MoreIcon />
                </IconButton>
              </ContextMenu>
            </div>
          );
        },
      });
    }

    return columns;
  }, [expandedAssetId, hasActiveReview, onAssetMenuAction, showActionsColumn, styles, t, tableMessages]);

  const tableWidth = useMemo(() => dataMapping.reduce((sum, { id }) => sum + (ASSET_REVIEWS_COLUMN_WIDTHS[id] ?? 120), 0), [dataMapping]);

  const tableDimensions = useMemo(
    () => ({
      minWidth: tableWidth,
      width: '100%',
    }),
    [tableWidth],
  );

  const renderColGroup = () => (
    <colgroup>
      {dataMapping.map(({ id }) => (
        <col key={id} style={{ width: ASSET_REVIEWS_COLUMN_WIDTHS[id] ?? 120 }} />
      ))}
    </colgroup>
  );

  const getScrollContainerRef = useCallback(
    (key: string) => (element: HTMLDivElement | null) => {
      if (element) {
        scrollContainerRefs.current.set(key, element);
        return;
      }

      scrollContainerRefs.current.delete(key);
    },
    [],
  );

  const handleSyncedScroll = useCallback(
    (sourceKey: string) => (event: React.UIEvent<HTMLDivElement>) => {
      if (isSyncingScrollRef.current) {
        return;
      }

      isSyncingScrollRef.current = true;
      const { scrollLeft } = event.currentTarget;

      scrollContainerRefs.current.forEach((_scrollContainer, key) => {
        if (key !== sourceKey) {
          scrollContainerRefs.current.get(key)?.scrollTo({ left: scrollLeft });
        }
      });

      isSyncingScrollRef.current = false;
    },
    [],
  );

  const handleRowClick = useCallback(
    (event: React.MouseEvent, asset: AssetRopaReview) => {
      if (shouldIgnoreRowActivation(event)) {
        return;
      }

      onExpandAsset(asset);
    },
    [onExpandAsset],
  );

  const handleRowKeyDown = useCallback(
    (event: React.KeyboardEvent, asset: AssetRopaReview) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }

      event.preventDefault();

      if (shouldIgnoreRowActivation(event)) {
        return;
      }

      onExpandAsset(asset);
    },
    [onExpandAsset],
  );

  const scrollSectionClassName = clsx(styles.assetReviewsSyncedScroll, {
    [styles.assetReviewsSyncedScrollWithActions]: showActionsColumn,
  });
  const rowScrollSectionClassName = clsx(scrollSectionClassName, styles.assetReviewsRowScroll);

  const renderHeader = () => (
    <div ref={getScrollContainerRef('header')} className={rowScrollSectionClassName} onScroll={handleSyncedScroll('header')}>
      <div className={styles.assetReviewsTableWide}>
        <table className={tableStyles.table} style={tableDimensions}>
          {renderColGroup()}
          <thead className={tableStyles.thead}>
            <tr>
              {dataMapping.map(({ id, isAddon, label }) => (
                <th
                  key={id}
                  className={clsx(tableStyles.th, isAddon && tableStyles.addonHeaderCell)}
                  id={`asset-reviews-${id}`}
                  scope='col'
                >
                  {isAddon ? null : label}
                </th>
              ))}
            </tr>
          </thead>
        </table>
      </div>
    </div>
  );

  const renderAssetRow = (asset: AssetRopaReview) => {
    const isExpanded = expandedAssetId === asset.id;
    const rowKey = `asset-${asset.id}`;

    return (
      <div key={asset.id} className={styles.assetReviewsAssetGroup}>
        <div ref={getScrollContainerRef(rowKey)} className={rowScrollSectionClassName} onScroll={handleSyncedScroll(rowKey)}>
          <div className={styles.assetReviewsTableWide}>
            <table className={tableStyles.table} style={tableDimensions}>
              {renderColGroup()}
              <tbody>
                <tr
                  aria-expanded={isExpanded}
                  aria-label={t(tableMessages.row_aria_label, { name: asset.assetName })}
                  className={clsx(tableStyles.tr, tableStyles.clickableTr, isExpanded && tableStyles.selectedRow)}
                  onClick={event => handleRowClick(event, asset)}
                  onKeyDown={event => handleRowKeyDown(event, asset)}
                  role='button'
                  tabIndex={0}
                >
                  {dataMapping.map(({ get, id, isAddon, width }) => {
                    const cellContent = get(asset);

                    return (
                      <td
                        key={id}
                        className={clsx(tableStyles.td, isAddon && styles.assetReviewsActionsCell)}
                        headers={`asset-reviews-${id}`}
                        width={width}
                      >
                        <span className={tableStyles.tdContent}>{cellContent}</span>
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        {isExpanded && <div className={styles.assetReviewsExpandedPanel}>{renderExpandedAsset(asset)}</div>}
      </div>
    );
  };

  if (assets.length === 0) {
    return (
      <div aria-label={t(tableMessages.aria_label)} className={styles.assetReviewsTableRoot} role='region'>
        <div className={styles.assetReviewsTableSurface}>
          <div className={tableStyles.noData}>{emptyStateContent}</div>
        </div>
      </div>
    );
  }

  const renderScrollFooter = () => (
    <div
      ref={getScrollContainerRef('footer')}
      aria-hidden
      className={clsx(styles.assetReviewsSyncedScroll, styles.assetReviewsScrollFooter)}
      onScroll={handleSyncedScroll('footer')}
    >
      <div className={styles.assetReviewsScrollFooterTrack} style={tableDimensions} />
    </div>
  );

  return (
    <div aria-label={t(tableMessages.aria_label)} className={styles.assetReviewsTableRoot} role='region'>
      <div className={styles.assetReviewsTableSurface}>
        {renderHeader()}
        {assets.map(renderAssetRow)}
        {renderScrollFooter()}
      </div>
    </div>
  );
};

export default AssetRopaReviewsTable;
